import { ArrowRight, RefreshCw, ShieldCheck, Zap } from "lucide-react";
import Image from "@/components/shared/image";
import { useAnalytics } from "@/lib/analytics";
import { useAuth } from "@/components/auth/AuthProvider";
import { useTranslation } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import Gif02Icon from "@hugeicons/core-free-icons/Gif02Icon";

const CTA_TEXT_GRADIENT = "bg-primary-gradient bg-clip-text text-transparent";
const CTA_GRADIENT_BG = "bg-primary-gradient";

const trustRowShell =
  "flex w-full max-w-xl flex-nowrap items-stretch overflow-x-auto rounded-full border border-border/50 bg-muted/25 shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden dark:border-border/40 dark:bg-muted/15 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]";

const trustRowItem =
  "flex min-w-0 shrink-0 items-center gap-1.5 px-2 py-1 sm:gap-1.5 sm:px-2.5 sm:py-1.5";

const trustRowIconWell =
  "flex size-6 shrink-0 items-center justify-center rounded-md bg-background/80 text-muted-foreground shadow-sm ring-1 ring-border/40 dark:bg-background/40 sm:size-6";

export default function PricingsPlansCta() {
  const { track } = useAnalytics();
  const { openAuth } = useAuth();
  const { translations: t } = useTranslation();
  const p = t.pricing;

  return (
    <section
      className={cn(
        "hidden", // Temporarily hidden as per request
        "mt-16 w-full overflow-visible sm:mt-20",
        "mb-8 lg:mb-10",
        "rounded-[1.75rem] border border-border/40",
        "bg-gradient-to-br from-primary/10 via-background to-primary/5",
        "shadow-[var(--soft-shadow)]",
        "dark:from-primary/15 dark:via-background dark:to-primary/10",
      )}
      aria-labelledby="pricings-plans-cta-heading"
    >
      <div className="flex flex-col gap-5 overflow-visible px-4 py-5 sm:gap-5 sm:px-6 sm:py-6 lg:flex-row lg:items-stretch lg:gap-0 lg:overflow-visible lg:px-5 lg:py-6 xl:px-6">
        <div className="relative z-[2] flex min-w-0 w-full flex-col lg:w-[40%] lg:max-w-none lg:shrink-0 lg:py-3 lg:pr-1 xl:py-4 xl:pr-2">
          <div
            className={cn(
              "mb-5 inline-flex w-fit max-w-full self-start items-center gap-2 rounded-full border border-white/70 px-3 py-1 text-xs font-medium sm:mb-6 sm:text-sm",
              "bg-white/50 shadow-sm backdrop-blur-md dark:border-white/15 dark:bg-white/10",
            )}
          >
            <HugeiconsIcon icon={Gif02Icon} size={18} className="shrink-0 text-primary dark:opacity-90" aria-hidden />
            <span className="text-muted-foreground">{p.plansCtaBadgeLead}</span>
            <span className="font-semibold text-primary">{p.plansCtaBadgeEmphasis}</span>
          </div>

          <h2
            id="pricings-plans-cta-heading"
            className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-[2.35rem] lg:leading-[1.15]"
          >
            {p.plansCtaTitleLead}{" "}
            <span className={CTA_TEXT_GRADIENT}>{p.plansCtaTitleGradient}</span>
          </h2>

          <div className="mt-6 max-w-xl space-y-5 text-pretty text-base leading-relaxed text-muted-foreground sm:mt-7 sm:space-y-6 sm:text-lg lg:max-w-none">
            <p>{p.plansCtaSubtitle}</p>
            <p>{p.plansCtaSubtitleParagraph2}</p>
          </div>

          <div
            role="list"
            aria-label={`${p.plansCtaBullet1}. ${p.plansCtaBullet3}`}
            className={cn(trustRowShell, "mt-7 max-w-full divide-x divide-border/55 sm:mt-8")}
          >
            <div role="listitem" className={cn(trustRowItem, "pl-2 sm:pl-2.5")}>
              <span className={trustRowIconWell}>
                <Zap className="size-3 sm:size-3.5" strokeWidth={2.25} aria-hidden />
              </span>
              <span className="whitespace-nowrap text-[11px] font-medium leading-tight text-foreground sm:text-xs">
                {p.plansCtaBullet1}
              </span>
            </div>
            <div role="listitem" className={cn(trustRowItem, "pr-2 sm:pr-2.5")}>
              <span className={trustRowIconWell}>
                <RefreshCw className="size-3 sm:size-3.5" strokeWidth={2.25} aria-hidden />
              </span>
              <span className="whitespace-nowrap text-[11px] font-medium leading-tight text-foreground sm:text-xs">
                {p.plansCtaBullet3}
              </span>
            </div>
          </div>

          <div className="relative mt-5 w-full max-w-full sm:mt-6 lg:max-w-md">
            <div
              className={cn(
                "pointer-events-none absolute -inset-x-2 -inset-y-3 rounded-full opacity-65 blur-xl",
                CTA_GRADIENT_BG,
              )}
              aria-hidden
            />
            <button
              type="button"
              className={cn(
                "relative flex w-full max-w-md items-center gap-2 rounded-full px-4 py-2.5 text-left shadow-md sm:gap-2.5 sm:px-5 sm:py-2.5",
                CTA_GRADIENT_BG,
                "text-white ring-1 ring-white/30 transition-[transform,filter] duration-200",
                "hover:brightness-[1.03] active:scale-[0.99] active:brightness-[0.98]",
              )}
              onClick={() => {
                track("pricing_plans_cta_clicked", { section: "pricing_page", placement: "plans_cta_banner" });
                openAuth();
              }}
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white/20 ring-1 ring-white/25">
                <HugeiconsIcon icon={Gif02Icon} size={18} color="#ffffff" aria-hidden />
              </span>
              <span className="min-w-0 flex-1 text-center text-xs font-bold tracking-tight sm:text-sm">
                {p.startFreeTrial}
              </span>
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white/25 ring-1 ring-white/35">
                <ArrowRight className="size-4" strokeWidth={2.5} aria-hidden />
              </span>
            </button>
          </div>

          <p className="mt-3.5 flex items-center gap-2 text-sm text-muted-foreground sm:mt-4">
            <ShieldCheck className="size-4 shrink-0 text-primary dark:text-primary/80" strokeWidth={2} aria-hidden />
            <span>{p.plansCtaTrust}</span>
          </p>
        </div>

        <div className="relative z-[1] flex w-full min-w-0 flex-col items-center justify-center overflow-visible lg:min-h-0 lg:w-[60%] lg:shrink-0 lg:self-stretch lg:pl-0">
          <div
            className="pointer-events-none absolute inset-0 -m-4 rounded-3xl bg-gradient-to-tr from-primary/15 via-transparent to-primary/10 blur-2xl dark:from-primary/10 dark:to-primary/8 lg:inset-x-0 lg:-m-2 lg:opacity-90"
            aria-hidden
          />
          <div className="relative z-[1] flex w-full justify-center overflow-visible">
            <Image
              src="/zapify-bot.webp"
              alt="Zapify AI automation bot for Instagram DM automation"
              width={850}
              height={650}
              className={cn(
                "relative h-auto w-full max-w-[14rem] object-contain object-center sm:max-w-[17rem]",
                "drop-shadow-[0_16px_40px_rgba(99,102,241,0.15)] dark:drop-shadow-[0_20px_48px_rgba(0,0,0,0.25)]",
                "lg:max-w-none lg:w-full lg:object-contain",
              )}
              loading="lazy"
              decoding="async"
              sizes="(max-width: 640px) 14rem, (max-width: 1024px) 17rem, 50vw"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
